import { IPv4, isValidCIDR, parseCIDR } from 'ipaddr.js';

// IPv4 addresses are only dotted four-part decimal, like the API.
// `ipaddr.parse` also takes shorthand such as `10.1` or `167772161`, which
// the API rejects.

export function isIpRange(value: string) {
  try {
    const isValid = isValidCIDR(value);
    if (!isValid) return false;
    const [address] = value.split('/');
    const [, range] = parseCIDR(value);
    return IPv4.isValidFourPartDecimal(address) && range > 0;
  } catch {
    return false;
  }
}

export function parseIpRange(value: string) {
  const [, range] = parseCIDR(value);
  return {
    count: 2 ** (32 - range),
  };
}

const HOSTNAME_REGEX = /^([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z][a-zA-Z0-9]+$/;
export function isHostname(value: string) {
  return HOSTNAME_REGEX.test(value);
}

export type HostType = 'ip' | 'ip_range' | 'hostname';

export function getHostType(value: string): HostType | null {
  if (IPv4.isValidFourPartDecimal(value)) {
    return 'ip';
  } else if (isIpRange(value)) {
    return 'ip_range';
  } else if (isHostname(value)) {
    return 'hostname';
  } else {
    return null;
  }
}

export function getMaybeHostType(value: string): HostType | null {
  const type = getHostType(value);
  if (type) return type;

  if (/^(\d+\.)+(\d+)*$/.test(value)) {
    return 'ip';
  } else if (/^(\d+\.)+(\d+)\/(\d)*$/.test(value)) {
    return 'ip_range';
  } else if (/^[a-zA-Z]+/.test(value)) {
    return 'hostname';
  } else {
    return null;
  }
}

export function isValidHost(value: string) {
  return getHostType(value) !== null;
}

/** One domain or IP address: what ASV sign-up covers, so not a range */
export function isSingleHost(value: string) {
  const type = getHostType(value);
  return type === 'ip' || type === 'hostname';
}
